import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogIn, Mail, Lock, AlertCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Tx } from "../context/AutoTranslateContext";

const GoogleGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.4 30.2 0 24 0 14.6 0 6.6 5.4 2.7 13.3l7.9 6.1C12.7 13.6 17.9 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.9 7.2l7.6 5.9c4.4-4.1 7.1-10.2 7.1-17.6z"/>
    <path fill="#FBBC05" d="M10.6 28.6c-.5-1.4-.8-2.9-.8-4.6s.3-3.2.8-4.6l-7.9-6.1C1 16.7 0 20.3 0 24s1 7.3 2.7 10.7l7.9-6.1z"/>
    <path fill="#34A853" d="M24 48c6.5 0 12-2.1 16-5.9l-7.6-5.9c-2.1 1.4-4.8 2.3-8.4 2.3-6.1 0-11.3-4.1-13.2-9.9l-7.9 6.1C6.6 42.6 14.6 48 24 48z"/>
  </svg>
);

const Login = () => {
  const { loginEmail, loginGoogle } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    const r = await loginEmail(email, password);
    setBusy(false);
    if (r.ok) nav("/"); else setError(r.error);
  };

  return (
    <div data-testid="login-page" className="max-w-md mx-auto px-4 py-16">
      <div className="bg-white rounded-3xl border border-gold/25 p-8 shadow-sm">
        <div className="text-center mb-8">
          <div className="inline-flex w-12 h-12 rounded-full bg-forest/10 text-forest items-center justify-center mb-3">
            <LogIn className="w-5 h-5" />
          </div>
          <h1 className="font-heading text-3xl text-forest font-bold mb-2">
            <Tx>Welcome back</Tx>
          </h1>
          <p className="text-sm text-mute"><Tx>Sign in to continue your journey.</Tx></p>
        </div>

        <button
          data-testid="login-google-btn"
          onClick={loginGoogle}
          className="w-full mb-5 flex items-center justify-center gap-3 px-4 py-3 rounded-full border border-forest/15 text-forest bg-white hover:bg-cream transition"
        >
          <GoogleGlyph />
          <span className="text-sm font-medium"><Tx>Continue with Google</Tx></span>
        </button>

        <div className="relative my-5">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-forest/10" /></div>
          <div className="relative flex justify-center">
            <span className="bg-white px-3 text-xs uppercase tracking-widest text-mute"><Tx>or</Tx></span>
          </div>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-mute"><Tx>Email</Tx></span>
            <div className="mt-1.5 relative">
              <Mail className="w-4 h-4 text-mute absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="login-email"
                type="email" autoComplete="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-full bg-cream border border-forest/10 text-ink outline-none focus:border-forest transition"
              />
            </div>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-mute"><Tx>Password</Tx></span>
            <div className="mt-1.5 relative">
              <Lock className="w-4 h-4 text-mute absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="login-password"
                type="password" autoComplete="current-password" required minLength={1}
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-full bg-cream border border-forest/10 text-ink outline-none focus:border-forest transition"
              />
            </div>
          </label>

          {error && (
            <div data-testid="login-error" className="flex items-start gap-2 text-sm text-clay bg-clay/10 rounded-2xl p-3">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <button
            data-testid="login-submit"
            type="submit" disabled={busy}
            className="w-full py-3 rounded-full bg-forest text-sand hover:bg-sage transition font-medium disabled:opacity-60"
          >
            {busy ? <Tx>Signing in…</Tx> : <Tx>Sign in</Tx>}
          </button>
        </form>

        <div className="mt-5 flex items-center justify-between text-xs text-mute">
          <Link data-testid="link-forgot" to="/forgot-password" className="hover:text-forest"><Tx>Forgot password?</Tx></Link>
          <Link data-testid="link-register" to="/register" className="hover:text-forest"><Tx>Create an account</Tx></Link>
        </div>

        {/* Self-serve recovery for paid buyers who never got the email */}
        <div className="mt-6 pt-5 border-t border-forest/10">
          <p className="text-[10px] uppercase tracking-widest text-gold mb-2 text-center"><Tx>Already paid but can't sign in?</Tx></p>
          <Link
            data-testid="link-recover"
            to="/recover-access"
            className="block text-center py-2.5 rounded-full bg-gold/15 text-gold hover:bg-gold/25 text-sm font-medium"
          >
            <Tx>Get my sign-in link →</Tx>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
